import { useState, useEffect, useCallback } from 'react';

/**
 * useGuidedTour — manages multi-step tooltip tours.
 *
 * Usage:
 *   const tour = useGuidedTour('locker-room', [
 *     { targetSelector: '.readiness-score', title: '...', body: '...', placement: 'bottom' },
 *     { targetSelector: '.quests-panel', title: '...', body: '...', placement: 'left' },
 *   ]);
 *
 * Returns: { currentStep, stepIndex, totalSteps, isActive, next, dismiss, startTour }
 *
 * @param {string} tourId — unique identifier persisted in localStorage
 * @param {Array<{ targetSelector: string, title: string, body: string, placement?: string }>} steps
 */
const MOBILE_BREAKPOINT = 768;

const useGuidedTour = (tourId, steps) => {
  const storageKey = 'slam_tour_' + tourId + '_done';

  const [stepIndex, setStepIndex] = useState(0);
  const [isActive, setIsActive] = useState(false);

  // Auto-start on mount if not previously completed (skip on mobile)
  useEffect(() => {
    if (window.innerWidth < MOBILE_BREAKPOINT) return;
    const done = localStorage.getItem(storageKey);
    if (!done) {
      const timer = setTimeout(() => {
        setStepIndex(0);
        setIsActive(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [storageKey]);

  const markComplete = useCallback(() => {
    localStorage.setItem(storageKey, 'true');
  }, [storageKey]);

  const next = useCallback(() => {
    if (stepIndex >= steps.length - 1) {
      // Last step — complete tour
      markComplete();
      setIsActive(false);
    } else {
      setStepIndex((i) => i + 1);
    }
  }, [stepIndex, steps.length, markComplete]);

  const dismiss = useCallback(() => {
    markComplete();
    setIsActive(false);
  }, [markComplete]);

  const startTour = useCallback(() => {
    if (window.innerWidth < MOBILE_BREAKPOINT) return;
    setStepIndex(0);
    setIsActive(true);
  }, []);

  const currentStep = isActive && steps[stepIndex] ? steps[stepIndex] : null;
  const totalSteps = steps.length;

  return {
    currentStep,
    stepIndex,
    totalSteps,
    isActive,
    next,
    dismiss,
    startTour,
  };
};

export default useGuidedTour;
